/**
 * Status badges (usuário) — fonte única do markup no lado do cliente.
 *
 * Os DADOS vêm sempre do PHP (`Usuario_Status::get_badges()` / `get_badges_batch()`,
 * entregues como `user_status_badges` na REST). Este arquivo é o único lugar do
 * JS que transforma esses dados em HTML — listagem de recursos, roster de turma
 * e modal de detalhes consomem daqui.
 *
 * ⚠️ Paridade: existe um único equivalente server-side,
 * `Usuario_Status::render_badges()` (usado no header do recurso, que é
 * renderizado no servidor). Mudou o markup aqui? Mude lá também.
 *
 * Formato de cada badge (ver Usuario_Status::get_badges()):
 *   { key, icon (Lucide), bg (hex), color (hex), title (string), url (string|'') }
 * `color` é a cor do ícone, calculada no PHP pelo fundo (Utils::icon_color_on:
 * escuro no amarelo/âmbar/laranja, branco no resto).
 *
 * @package Labead
 */

/**
 * Escapa texto para uso dentro de atributo HTML.
 *
 * @param {*} value Valor bruto.
 * @returns {string}
 */
function escAttr(value) {
	return String(value ?? '')
		.replace(/&/g, '&amp;')
		.replace(/"/g, '&quot;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;');
}

/** Atributos htmx usados quando o badge vira link. */
const HX_ATTRS = 'hx-boost="true" hx-target="#lb_body" hx-swap="innerHTML show:window:top"';

/**
 * Um badge quadrado compacto (ícone só). Vira `<a>` quando tem url.
 *
 * @param {Object} badge Badge vindo da REST.
 * @returns {string} HTML.
 */
function badgeIcon(badge) {
	const tag = badge.url ? 'a' : 'span';
	const attrs = badge.url ? ` href="${escAttr(badge.url)}" ${HX_ATTRS}` : '';

	return (
		`<${tag}${attrs} class="lw:inline-flex lw:items-center lw:justify-center lw:p-0.5 lw:rounded lw:shrink-0"` +
		` style="background:${escAttr(badge.bg)}" title="${escAttr(badge.title)}">` +
		`<i data-lucide="${escAttr(badge.icon)}" stroke-width="2.5" class="lw:w-3.5 lw:h-3.5" style="color:${escAttr(badge.color || '#ffffff')}"></i>` +
		`</${tag}>`
	);
}

/**
 * Cluster inline de badges — colados entre si, sem gap e sem sobrepor.
 *
 * O wrapper existe para que um `gap-*` do flex pai se aplique uma vez só ao
 * grupo inteiro, e não entre cada badge.
 *
 * `extraHtml` (ícone de status geral do recurso: ativo, bloqueado, expirado…)
 * NÃO entra colado no grupo: ele é outra coisa — o estado do cadastro, não uma
 * pendência do aluno — então fica num grupo próprio, separado por um gap. Sem
 * isso o check verde de "ativo" se confunde com os badges verdes de
 * financeiro/contrato que ficam ao lado.
 *
 * @param {Array}  badges    Lista de badges.
 * @param {string} [extraHtml] HTML do ícone de status geral do recurso.
 * @returns {string} HTML ('' quando não há nada a mostrar).
 */
function cluster(badges, extraHtml) {
	const icons = (Array.isArray(badges) ? badges : []).map(badgeIcon).join('');
	const extra = extraHtml || '';

	if (!icons && !extra) return '';

	const group = icons ? `<span class="lw:inline-flex lw:items-center lw:shrink-0">${icons}</span>` : '';

	// Sem status geral (roster de turma, modal) não há o que separar: um wrapper só.
	if (!extra) return group;

	return `<span class="lw:inline-flex lw:items-center lw:gap-1.5 lw:shrink-0">${group}${extra}</span>`;
}

/**
 * Linhas verticais com ícone + título (bloco do modal de detalhes).
 *
 * @param {Array} badges Lista de badges.
 * @returns {string} HTML ('' quando não há nada a mostrar).
 */
function rows(badges) {
	return (Array.isArray(badges) ? badges : [])
		.map((badge) => {
			const tag = badge.url ? 'a' : 'div';
			const attrs = badge.url ? ` href="${escAttr(badge.url)}" ${HX_ATTRS}` : '';
			const arrow = badge.url
				? '<i data-lucide="chevron-right" class="lw:w-3.5 lw:h-3.5 lw:text-theme-muted lw:shrink-0 lw:mt-0.5"></i>'
				: '';

			return (
				`<${tag}${attrs} class="lw:flex lw:items-start lw:gap-2 lw:-mx-1.5 lw:px-1.5 lw:py-1 lw:rounded-md lw:no-underline${badge.url ? ' lb-btn-hover' : ''}">` +
				`<span class="lw:inline-flex lw:items-center lw:justify-center lw:p-0.5 lw:rounded lw:shrink-0 lw:mt-0.5" style="background:${escAttr(badge.bg)}">` +
				`<i data-lucide="${escAttr(badge.icon)}" stroke-width="2.5" class="lw:w-3.5 lw:h-3.5" style="color:${escAttr(badge.color || '#ffffff')}"></i></span>` +
				`<span class="lw:text-sm lw:break-words lw:min-w-0 lw:leading-relaxed lw:flex-1">${escAttr(badge.title)}</span>${arrow}` +
				`</${tag}>`
			);
		})
		.join('');
}

/**
 * Tamanho do ponto de status por preset de avatar — [ ponto, ícone ].
 * Espelha `Avatar::STATUS_DOT_SIZES` (PHP); mudou aqui, mude lá.
 *
 * O ponto não tem borda: os tamanhos já descontam os 4px que a antiga
 * `border-2` ocupava, então a área útil do ícone continua a mesma e o ponto
 * cobre menos do avatar.
 */
const STATUS_DOT_SIZES = {
	xs: ['lw:w-2.5 lw:h-2.5', 'lw:w-2 lw:h-2'],
	sm: ['lw:w-3 lw:h-3', 'lw:w-2.5 lw:h-2.5'],
	md: ['lw:w-4 lw:h-4', 'lw:w-3 lw:h-3'],
	lg: ['lw:w-5 lw:h-5', 'lw:w-3.5 lw:h-3.5'],
	xl: ['lw:w-6 lw:h-6', 'lw:w-4 lw:h-4'],
};

/**
 * Ponto de status geral do recurso (Ativo/Bloqueado/Expirado/…), no canto
 * INFERIOR direito do avatar/thumbnail — como indicador de status de chat.
 * O ponto verde de presença fica no canto superior direito, então os dois
 * convivem sem se sobrepor.
 *
 * O elemento que envolve a <img> precisa ter `lw:relative` colado nela.
 *
 * @param {Object} badge  Entrada de `statusBadges` (icon, icon_bg, icon_text, label).
 * @param {string} [size] Preset de tamanho do avatar (xs/sm/md/lg/xl).
 * @param {string} [pos]  Classes de posicionamento. O padrão joga o ponto para
 *                        FORA do canto inferior direito, para ele cobrir o
 *                        mínimo possível da imagem. Thumbnail grande (card) usa
 *                        um pos próprio: lá o `overflow-hidden` do card cortaria
 *                        o que passasse da borda de baixo.
 * @returns {string} HTML ('' quando o status não tem ícone).
 */
function statusDot(badge, size = 'md', pos = 'lw:-bottom-1.5 lw:-right-1.5') {
	if (!badge || !badge.icon) return '';

	// Ativo/publish é o estado normal: não vale ocupar a imagem para dizer que
	// está tudo como deveria. O ponto existe para chamar atenção do que fugiu do
	// padrão (bloqueado, expirado, rascunho, arquivado…).
	if (badge.status === 'publish') return '';

	const [dotSize, iconSize] = STATUS_DOT_SIZES[size] || STATUS_DOT_SIZES.md;

	return (
		`<span class="lw:absolute ${pos} lw:inline-flex lw:items-center lw:justify-center ${dotSize}` +
		` lw:rounded-full ${escAttr(badge.icon_bg || 'lw:bg-gray-500')}"` +
		` title="${escAttr(badge.label || '')}">` +
		`<i data-lucide="${escAttr(badge.icon)}" stroke-width="3" class="${iconSize} ${escAttr(badge.icon_text || 'lw:text-white')}"></i>` +
		`</span>`
	);
}

/**
 * Ponto verde de presença ("online agora"), sobreposto ao avatar.
 * O elemento que envolve a <img> precisa ter `lw:relative` colado nela
 * (sem padding entre os dois), senão o ponto desencontra do canto.
 *
 * @param {boolean} isOnline Se o usuário está online.
 * @param {string}  [size]   Classes de tamanho do ponto.
 * @returns {string} HTML ('' quando offline).
 */
function onlineDot(isOnline, size = 'lw:w-2.5 lw:h-2.5') {
	if (!isOnline) return '';

	// Sem o title o ponto verde é um enfeite: nada diz que ele significa "está
	// online agora". String vem do PHP (lb_data.strings) para não fixar texto
	// traduzível no bundle.
	const label = escAttr((window.lb_data && lb_data.strings && lb_data.strings.online) || 'Online agora');

	return (
		`<span class="lw:absolute lw:-top-0.5 lw:-right-0.5 ${size} lw:rounded-full lw:bg-green-500 lw:border-2 lw:border-theme-surface"` +
		` title="${label}" aria-label="${label}" role="img"></span>`
	);
}

window.lbStatusBadges = { cluster, rows, onlineDot, statusDot, badgeIcon };

export { cluster, rows, onlineDot, statusDot, badgeIcon };
